import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { miners, MYSTERY_BOXES_PRIZES } from '../../utils/constants';
import { getMysteryBoxContract } from './MysteryBoxPage';
import { ethers } from 'ethers';
import Text from '../../components/Text';
import Button from '../../components/Button';
import CoordinatePopup from './CoordinatePopup';
import { notifyError } from '../../hooks/utils';

const PopupOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const PopupContent = styled.div`
    background: #fff;
    padding: 2rem;
    border-radius: 8px;
    max-width: 800px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
    
    @media (max-width: 768px) {
        padding: 1rem;
        width: 95%;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #507c83;
`;

const Title = styled.h2`
    color: #507c83;
    font-size: 2rem;
    margin-bottom: 2rem;
    text-align: center;
    font-family: "Press Start 2P", monospace;

    @media (max-width: 768px) {
        font-size: 1.5rem;
        margin-bottom: 1.5rem;
    }
`;

const ItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 2rem;
    margin-bottom: 1rem;
    padding: 1rem;
    border: 2px solid #507c83;
    border-radius: 4px;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
        padding: 1.5rem 1rem;
    }
`;

const MinerImage = styled.img`
    width: 100px;
    height: 100px;
    object-fit: contain;

    @media (max-width: 768px) {
        width: 120px;
        height: 120px;
    }
`;

const MinerName = styled(Text)`
    flex: 1;
    font-family: "Press Start 2P", monospace;
    color: #507c83;
    font-size: 1.2em;

    @media (max-width: 768px) {
        font-size: 1em;
        margin: 0.5rem 0;
    }
`;

const ClaimButton = styled(Button)`
    background: #507c83;
    color: white;
    padding: 1rem 2rem;
    font-family: "Press Start 2P", monospace;
    font-size: 1.2em;
    min-width: 150px;
    height: 50px;
    &:hover {
        background: #3a5d63;
    }

    @media (max-width: 768px) {
        width: 100%;
        max-width: 200px;
        height: 45px;
        font-size: 1em;
    }
`;

interface PendingItemsPopupProps {
    onClose: () => void;
}

const PendingItemsPopup: React.FC<PendingItemsPopupProps> = ({ onClose }) => {
    const [pendingMiners, setPendingMiners] = useState<{ pendingBoxId: number; miner: any }[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedMiner, setSelectedMiner] = useState<{ pendingBoxId: number; miner: any } | null>(null);
    const [showCoordinatePopup, setShowCoordinatePopup] = useState(false);

    useEffect(() => {
        fetchPendingItems();
    }, []);

    const fetchPendingItems = async () => {
        try {
            if (!(window as any).ethereum) throw new Error("No wallet found");
            const provider = new ethers.providers.Web3Provider((window as any).ethereum);
            const signer = provider.getSigner();
            const contract = getMysteryBoxContract(signer);
            const account = await signer.getAddress();

            // Get total boxes bought
            const totalBoxes = await contract.userBoxesBought(account);
            const pendingItems = [];

            // Loop through each box and get the miner ID
            for (let i = 0; i < totalBoxes.toNumber(); i++) {
                const pendingBoxId = await contract.userBoxesBoughtIds(account, i);
                const pendingBox = await contract.pendingBoxes(pendingBoxId);
                
                if (!pendingBox.claimed) {
                    const miner = MYSTERY_BOXES_PRIZES[pendingBox.prizeWonId.toNumber()];
                    if (miner) {
                        pendingItems.push({
                            pendingBoxId: pendingBoxId.toNumber(),
                            miner
                        });
                    }
                }
            }

            setPendingMiners(pendingItems);
        } catch (err) {
            console.error("Error fetching pending items:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleClaim = async (pendingBoxId: number, x: number, y: number) => {
        try {
            if (!(window as any).ethereum) throw new Error("No wallet found");
            const provider = new ethers.providers.Web3Provider((window as any).ethereum);
            const signer = provider.getSigner();
            const contract = getMysteryBoxContract(signer);
            const account = await signer.getAddress();

            // Call the claimPrize function with coordinates
            const tx = await contract.claimPrize(pendingBoxId, account, x, y);
            await tx.wait();

            // Close coordinate popup and refresh the list
            setShowCoordinatePopup(false);
            setSelectedMiner(null);
            fetchPendingItems();
        } catch (err) {
            notifyError("Error claiming prize: " + (err as any).message);
        }
    };

    const openCoordinatePopup = (pendingBoxId: number, miner: any) => {
        setSelectedMiner({ pendingBoxId, miner });
        setShowCoordinatePopup(true);
    };

    return (
        <PopupOverlay onClick={onClose}>
            <PopupContent onClick={e => e.stopPropagation()}>
                <CloseButton onClick={onClose}>×</CloseButton>
                <Title>PENDING ITEMS</Title>
                {loading ? (
                    <Text>Loading pending items...</Text>
                ) : pendingMiners.length === 0 ? (
                    <Text>No pending items to claim</Text>
                ) : (
                    pendingMiners.map(({ pendingBoxId, miner }) => (
                        <ItemRow key={pendingBoxId}>
                            <MinerImage src={miner.gif} alt={miner.name} />
                            <MinerName>{miner.name}</MinerName>
                            <ClaimButton
                                onClick={() => openCoordinatePopup(pendingBoxId, miner)}
                                variant="pixelAction"
                                size="md"
                                shape="square"
                            >
                                CLAIM
                            </ClaimButton>
                        </ItemRow>
                    ))
                )}

                {showCoordinatePopup && selectedMiner && (
                    <CoordinatePopup
                        onClose={() => {
                            setShowCoordinatePopup(false);
                            setSelectedMiner(null);
                        }}
                        onConfirm={(x, y) => handleClaim(selectedMiner.pendingBoxId, x, y)}
                        minerName={selectedMiner.miner.name}
                    />
                )}
            </PopupContent>
        </PopupOverlay>
    );
};

export default PendingItemsPopup; 